/**
 * Cookie consent storage + GA4 and Meta Pixel loaders.
 *
 * Neither tag is loaded until the user explicitly accepts. This module owns
 * the localStorage key and the dynamic script injection — the banner
 * component is pure UI on top of these primitives.
 */

/**
 * Bump the version whenever the banner asks consent for new purposes: a
 * choice recorded under the old wording must be asked again, not reused.
 */
const STORAGE_KEY = "altair_cookie_consent_v2";
const GA_MEASUREMENT_ID = "G-M7R5HRP10G";
const META_PIXEL_ID = "2206059763455815";

/** The `fbq` command queue that Meta's base pixel snippet defines. */
type MetaPixel = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: MetaPixel;
  loaded: boolean;
  version: string;
};

declare global {
  interface Window {
    /** Command queue read by gtag.js. Only exists after consent. */
    dataLayer?: unknown[];
    /**
     * Set by `loadGA4()`, so it is undefined until the user accepts. Callers
     * must use `window.gtag?.(...)` and treat a missing function as
     * "not tracked".
     */
    gtag?: (...args: unknown[]) => void;
    /**
     * Set by `loadMetaPixel()`. Like `gtag` it is undefined until the user
     * accepts, and it stays undefined under Global Privacy Control, so call
     * it as `window.fbq?.(...)`.
     */
    fbq?: MetaPixel;
    _fbq?: MetaPixel;
  }
  interface Navigator {
    /** Global Privacy Control. Not in TypeScript's DOM lib yet. */
    globalPrivacyControl?: boolean;
  }
}

export type ConsentValue = "accepted" | "declined";

export function readConsent(): ConsentValue | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "accepted" || v === "declined" ? v : null;
  } catch {
    return null;
  }
}

export function writeConsent(value: ConsentValue): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // localStorage unavailable (private mode, quota) — fail silently.
  }
}

let ga4Loaded = false;

/**
 * Inject the GA4 gtag script and initialize. Idempotent — safe to call
 * multiple times. Must only be called after consent; see CookieConsent.tsx
 * for the call sites.
 */
export function loadGA4(): void {
  if (ga4Loaded) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  ga4Loaded = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  // Must push the classic `arguments` object, not a rest-args array. gtag.js
  // only executes `Arguments` entries on the dataLayer as commands; a plain
  // array is routed to the legacy GTM method-call branch and silently
  // dropped, so `js`/`config` would never run and no hit would be sent.
  // Assigned to `window` so the CTA buttons' `window.gtag?.("event", ...)`
  // calls reach the queue.
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params -- gtag.js needs a real Arguments object
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID);
}

let metaPixelLoaded = false;

/**
 * Inject Meta's fbevents.js and initialize the pixel. Same contract as
 * `loadGA4()`: idempotent, and only called after consent. Never loads when
 * the browser sends Global Privacy Control, which the Privacy Policy honors
 * as an opt-out of sharing for cross-context behavioral advertising.
 */
export function loadMetaPixel(): void {
  if (metaPixelLoaded) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (window.navigator?.globalPrivacyControl === true) return;
  metaPixelLoaded = true;

  // Same stub as Meta's base snippet: fbevents.js replays `queue` (raw
  // Arguments objects) once it loads, then takes over through `callMethod`.
  const fbq = function () {
    // eslint-disable-next-line prefer-rest-params -- queued exactly as Meta's snippet does
    const args = arguments;
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as MetaPixel;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;

  const script = document.createElement("script");
  script.async = true;
  script.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(script);

  // Off, so Meta gets page views and the booking events only, not the button
  // text and page metadata that automatic configuration collects.
  fbq("set", "autoConfig", false, META_PIXEL_ID);
  fbq("init", META_PIXEL_ID);
  fbq("track", "PageView");
}
